/**
 * sessionStorage, localStorage, cookie storage, etc.
 */

export namespace Storage {
    export function hasStorage(): boolean {
        return (
            localStorage &&
            sessionStorage &&
            typeof localStorage.getItem === "function" &&
            typeof sessionStorage.getItem === "function"
        );
    }

    /**
     * Localstorage (w/ fallback to cookies)
     */

    export function getLocalStorage(key: string) {
        if (!hasStorage) {
            return getCookie(key);
        }
        return localStorage.getItem(key);
    }

    export function setLocalStorage(key: string, value: string) {
        if (!hasStorage) {
            setCookie(key, value);
        }

        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // Don't store for private browsing
            // tslint:disable-next-line:no-console
            console.log(
                "Local Storage failed, check if browsing in Safari private window."
            );
        }
    }

    export function removeLocalStorage(key: string) {
        if (!hasStorage) {
            removeCookie(key);
        }
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Don't remove for private browsing
            // tslint:disable-next-line:no-console
            console.log(
                "Local Storage failed, check if browsing in Safari private window."
            );
        }
    }

    /**
     * Sessionstorage (w/ fallback to cookies)
     */

    export function getSessionStorage(key: string) {
        if (!hasStorage) {
            return getCookie(key);
        }
        return sessionStorage.getItem(key);
    }

    export function setSessionStorage(key: string, value: string) {
        if (!hasStorage) {
            setCookie(key, value);
        }
        try {
            sessionStorage.setItem(key, value);
        } catch (e) {
            // Don't store for private browsing
            // tslint:disable-next-line:no-console
            console.log(
                "Session Storage failed, check if browsing in Safari private window."
            );
        }
    }

    export function removeSessionStorage(key: string) {
        if (!hasStorage) {
            removeCookie(key);
        }
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            // Don't remove for private browsing
            // tslint:disable-next-line:no-console
            console.log(
                "Session Storage failed, check if browsing in Safari private window."
            );
        }
    }

    /**
     * Cookie
     */

    export function getCookie(key: string) {
        const encodedName = encodeURIComponent(key).replace(
            /[\-\.\+\*]/g,
            "\\$&"
        );
        const regex = new RegExp(
            "(?:(?:^|.*;)\\s*" + encodedName + "\\s*\\=\\s*([^;]*).*$)|^.*$"
        );
        return decodeURIComponent(document.cookie.replace(regex, "$1")) || null;
    }

    export function setCookie(
        key: string,
        value: string,
        days?: number,
        sameSite?: string
    ) {
        let expires = "";
        const sameSiteString = sameSite
            ? `; SameSite=${
                  (sameSite && sameSite === "Strict") || sameSite === "Lax"
                      ? sameSite
                      : "None"
              }`
            : "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie =
            key + "=" + value + expires + sameSiteString + "; path=/";
    }

    export function removeCookie(key: string) {
        setCookie(key, "", -1);
    }
}
